import { IncomingHttpHeaders, IncomingMessage, ServerResponse } from 'node:http'
import { normaliseApiUrl, sanitizeDynamicPaths } from './processHttpPaths'

export const DATADOG_LOG_META_KEY = 'meta'
export const AVIOS_DATADOG_CLIENT_SERVICE_NAME = 'spend-rewardplatform-avios-client'
export const WHITELABEL_DATADOG_CLIENT_SERVICE_NAME = 'spend-rewardplatform-whitelabel-client'
export const AVIOS_DATADOG_SERVICE_NAME = 'spend-rewardplatform-avios-next-server'
export const WHITELABEL_DATADOG_SERVICE_NAME = 'spend-rewardplatform-whitelabel-next-server'

export type DatadogServerServiceName =
  | typeof AVIOS_DATADOG_SERVICE_NAME
  | typeof WHITELABEL_DATADOG_SERVICE_NAME

export type DatadogClientServiceName =
  | typeof AVIOS_DATADOG_CLIENT_SERVICE_NAME
  | typeof WHITELABEL_DATADOG_CLIENT_SERVICE_NAME

const isDatadogServerServiceName = (service?: string): service is DatadogServerServiceName =>
  service === AVIOS_DATADOG_SERVICE_NAME || service === WHITELABEL_DATADOG_SERVICE_NAME

const isDatadogClientServiceName = (service?: string): service is DatadogClientServiceName =>
  service === AVIOS_DATADOG_CLIENT_SERVICE_NAME ||
  service === WHITELABEL_DATADOG_CLIENT_SERVICE_NAME

export const getDefaultDatadogServiceName = (): DatadogServerServiceName => {
  const platformType = process.env.NEXT_PUBLIC_PLATFORM_TYPE
  if (platformType === 'avios') {
    return AVIOS_DATADOG_SERVICE_NAME
  }

  return WHITELABEL_DATADOG_SERVICE_NAME
}

export const getDefaultDatadogClientServiceName = (): DatadogClientServiceName => {
  const platformType = process.env.NEXT_PUBLIC_PLATFORM_TYPE
  if (platformType === 'avios') {
    return AVIOS_DATADOG_CLIENT_SERVICE_NAME
  }

  return WHITELABEL_DATADOG_CLIENT_SERVICE_NAME
}

export const getDatadogServiceName = (): DatadogServerServiceName =>
  isDatadogServerServiceName(process.env.DD_SERVICE)
    ? process.env.DD_SERVICE
    : getDefaultDatadogServiceName()

export const getDatadogClientServiceName = (): DatadogClientServiceName =>
  isDatadogClientServiceName(process.env.NEXT_PUBLIC_DD_SERVICE)
    ? process.env.NEXT_PUBLIC_DD_SERVICE
    : getDefaultDatadogClientServiceName()

export type DatadogLogErrorContext = {
  message: string
  stack?: string
  code?: string
  description: string
  httpStatus?: number
  [key: string]: any
}

export type DatadogLogMetaContext = {
  partner: 'EI' | 'BA' | 'IB'
  platformType: 'avios' | 'whitelabel'
  correlationId?: string | null
  [key: string]: any
}

export type DatadogLogHttpContext = {
  method?: string
  status_code?: number
  url?: string
}

type DatadogLogBaseContext = {
  dd: {
    service: DatadogServerServiceName
    env: string
    version: string
  }
  service: DatadogServerServiceName
  timestamp: number
  [key: string]: any
}

type DatadogLogBaseContextWithoutDatadogContext = Omit<DatadogLogBaseContext, 'dd'>

export type DatadogLoggerMetaOptions = {
  includeDatadogContext?: boolean
}

export type DatadogLogContext = (
  | DatadogLogBaseContext
  | DatadogLogBaseContextWithoutDatadogContext
) & {
  [DATADOG_LOG_META_KEY]: DatadogLogMetaContext
  level?: 'info' | 'warn' | 'error' | 'critical'
  http?: DatadogLogHttpContext
  error?: DatadogLogErrorContext
  headers?: IncomingHttpHeaders
}

export const createDatadogMetaContext = (meta: DatadogLogMetaContext) => ({
  [DATADOG_LOG_META_KEY]: meta,
})

const getDatadogEnv = (): string =>
  process.env.DD_ENV || process.env.NEXT_PUBLIC_DEPLOYMENT || process.env.NODE_ENV || 'development'

export function getDatadogLoggerBaseMeta(
  obj?: { [key: string]: any },
  options?: { includeDatadogContext?: true }
): DatadogLogBaseContext
export function getDatadogLoggerBaseMeta(
  obj: { [key: string]: any },
  options: { includeDatadogContext: false }
): DatadogLogBaseContextWithoutDatadogContext
export function getDatadogLoggerBaseMeta(
  obj: { [key: string]: any },
  options: DatadogLoggerMetaOptions
): DatadogLogBaseContext | DatadogLogBaseContextWithoutDatadogContext
export function getDatadogLoggerBaseMeta(
  obj: { [key: string]: any } = {},
  options: DatadogLoggerMetaOptions = {}
): DatadogLogBaseContext | DatadogLogBaseContextWithoutDatadogContext {
  const service = getDatadogServiceName()
  const baseMeta = {
    service,
    timestamp: Date.now(),
    ...obj,
  }

  if (options.includeDatadogContext === false) {
    return baseMeta
  }

  return {
    ...baseMeta,
    dd: {
      service,
      env: getDatadogEnv(),
      version: '1.0',
    },
  }
}

const filterSensitiveHeaders = (headers?: IncomingHttpHeaders): IncomingHttpHeaders | undefined => {
  if (!headers) return undefined

  const filtered: IncomingHttpHeaders = {}

  Object.entries(headers).forEach(([key, value]) => {
    const lowerKey = key.toLowerCase()

    const isSensitiveHeader =
      lowerKey === 'cookie' ||
      lowerKey === 'authorization' ||
      lowerKey.includes('token') ||
      lowerKey.includes('secret')

    if (!isSensitiveHeader) {
      filtered[key] = value
    }
  })

  return filtered
}

export const createServerLoggerMeta = (
  req?: IncomingMessage,
  res?: ServerResponse,
  additionalMeta?: Record<string, any> | null,
  options: DatadogLoggerMetaOptions = {}
): DatadogLogContext => {
  const reqUrl = req?.url
    ? new URL(normaliseApiUrl(req.url), `http://${req.headers.host}`)
    : undefined

  return {
    ...getDatadogLoggerBaseMeta({}, options),
    ...createDatadogMetaContext({
      partner: req?.headers['x-partner'],
      opCo: req?.headers['x-partner'],
      platformType: req?.headers['x-platform-type'],
      correlationId: req?.headers['x-correlation-id'],
      req: {
        httpVersion: req?.httpVersion,
        method: req?.method,
        url: reqUrl?.pathname,
        path: reqUrl ? reqUrl.pathname : undefined,
        query: reqUrl ? Object.fromEntries(reqUrl.searchParams) : undefined,
        headers: filterSensitiveHeaders(req?.headers),
      },
      res: {
        statusCode: res?.statusCode,
      },
      sanitizedPath: reqUrl ? sanitizeDynamicPaths(reqUrl.pathname) : undefined,
      ...additionalMeta,
    } as DatadogLogMetaContext),
  }
}
